import { Reveal } from "@/components/motion/Reveal";

/**
 * HonestTransparency — replaces theatrical "live" proof widgets.
 *
 * We don't fake numbers. Until we have customer-attributed results we can publish,
 * this section says exactly what is and isn't true today. Honesty out-converts theatre.
 */
export function HonestTransparency() {
  return (
    <section className="relative">
      <div className="max-w-4xl mx-auto px-4 py-20 md:py-28">
        <Reveal stagger={60} className="text-center space-y-3 max-w-2xl mx-auto mb-12">
          <p className="text-[10px] font-mono uppercase tracking-widest text-primary">
            Transparency
          </p>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground tracking-tight">
            What this is. What it isn't. Yet.
          </h2>
          <p className="text-sm md:text-base text-foreground/75 max-w-xl mx-auto">
            Most AI SDR pages show ticking counters and rotating "live" demos. Ours doesn't.
            Here is the actual state of the product, today.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          <Card
            tone="positive"
            label="What works today"
            items={[
              "Drop a URL → factually-grounded cold email in seconds",
              "Sources every claim from your site — never invents facts",
              "Real Google Meet / Microsoft Teams booking links",
              "Built-in deliverability infrastructure (warm-up, placement tests, suppression)",
              "$49/mo, first qualified meeting free, cancel anytime",
            ]}
          />
          <Card
            tone="caveat"
            label="What we're still earning"
            items={[
              "Public, named case studies — we're onboarding our first cohort",
              "Public reply-rate benchmarks from our own engagement data",
              "SOC 2 — on the roadmap, not yet certified",
              "A category-defining moment — we'd rather show it than claim it",
            ]}
          />
        </div>

        <p className="text-center text-xs text-foreground/55 mt-8 max-w-md mx-auto">
          If you'd rather see a live demo than read this, scroll back up and drop your URL.
          That's the only "live" thing on this page — and it actually is.
        </p>
      </div>
    </section>
  );
}

function Card({
  tone,
  label,
  items,
}: {
  tone: "positive" | "caveat";
  label: string;
  items: string[];
}) {
  const accent =
    tone === "positive"
      ? "border-primary/40 bg-primary/[0.04]"
      : "border-border bg-background/60";
  const dot =
    tone === "positive"
      ? "bg-primary shadow-[0_0_8px_hsl(var(--primary)/0.6)]"
      : "bg-foreground/40";
  return (
    <div
      className={`rounded-2xl border ${accent} backdrop-blur-xl p-6 md:p-7 space-y-4`}
    >
      <p className="text-[10px] font-mono uppercase tracking-widest text-foreground/70">
        {label}
      </p>
      <ul className="space-y-2.5">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm text-foreground/85 leading-relaxed">
            <span aria-hidden className={`h-1.5 w-1.5 rounded-full mt-1.5 shrink-0 ${dot}`} />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
